import ErrorMessage from '@/components/ErrorMessage';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { FeatureRuleNameList, UpdateUserSetting, UserSetting } from '@/models/user-setting.model';
import { RoleEnum, User } from '@/models/user.model';
import { getUserSetting, updateUserSetting } from '@/services/user-setting.service';
import { getCitiesLookup } from '@/services/city.service';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { Divider, Form, Stack, Schema, Message, Checkbox, IconButton, Col, Grid, Row, FormInstance, InputGroup, TagPicker } from 'rsuite';
import { useNavigate, useParams } from 'react-router-dom';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import PageContent from '@/components/PageContent';
import FetchingSelect from '@/components/FetchingSelect';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { MdContentCopy, MdContentPaste } from 'react-icons/md';
import { Icon } from '@rsuite/icons';

const modelSchemaEdit = Schema.Model<UpdateUserSetting>({
  id: Schema.Types.NumberType(),
  userId: Schema.Types.NumberType(),
  cityId: Schema.Types.NumberType(),
  firebaseToken: Schema.Types.StringType(),
  firebaseTokenIsInvalid: Schema.Types.BooleanType(),
  language: Schema.Types.StringType(),
  nationality: Schema.Types.StringType(),
});

const emptyModel: UpdateUserSetting = {
  id: undefined,
  userId: undefined,
  cityId: undefined,
  language: '',
  firebaseToken: '',
  firebaseTokenIsInvalid: undefined,
  nationality: '',
  restrictedFeatures: [],
};

interface UserSettingFormProps {
  user?: User;
}

const UserSettingForm = (props: UserSettingFormProps) => {

  const { user } = props;
  const [formValue, setFormValue] = useState<UpdateUserSetting>(emptyModel);
  const [formError, setFormError] = useState({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);
  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;

  useEffect(() => {
    if (id) {
      fetchData();
    } else {
      setFormValue(emptyModel);
    }
  }, [id]);

  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<UserSetting> = await getUserSetting(id);
        if (result.success) {
          const castedModel: UpdateUserSetting = { ...result.result };
          setFormValue(castedModel);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching user settings:${errorModel?.errorMessage}`);
      }
      finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log(formError);
      return;
    }
    setLoading(true);
    setErrorModel(undefined);
    try {
      const result: BaseResponseModel<UserSetting> = await updateUserSetting(formValue);
      if (result.success) {
        nav('/users');
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }
    finally {
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  if (!user || user.role != RoleEnum.User) {
    return (
      <Message type="info">The User Setting is currently only available to users with the Customer role.</Message>
    );
  }

  return (
    <PageContent>
      <Form name='upsert-user-setting' fluid
        ref={formRef}
        onChange={handleFormChange}
        onCheck={setFormError}
        formValue={formValue}
        model={modelSchemaEdit}>

        <Grid w='100%' >
          <Row>
            <Col span={{xs: 24, xl:12}}>
              <Form.Group>
                <Form.Label>Firebase Token</Form.Label>
                <InputGroup inside>
                  <InputGroup.Button onClick={async () => { 
                    const text = await navigator.clipboard.readText(); 
                    setFormValue(pre => ({...pre, firebaseToken: text})); 
                  }}>
                    <Icon as={MdContentPaste} />
                  </InputGroup.Button>
                  <Form.Control name="firebaseToken" value={formValue.firebaseToken ?? ''} />
                  <InputGroup.Button onClick={() => navigator.clipboard.writeText(formValue.firebaseToken || '')}>
                    <Icon as={MdContentCopy} />
                  </InputGroup.Button>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col span={{xs: 24, xl:12}}>
              <Form.Group>
                <Form.Label>Firebase Token Is Invalid</Form.Label>
                <Form.Control name="firebaseTokenIsInvalid" accepter={Checkbox}
                  checked={!!formValue.firebaseTokenIsInvalid}
                  onChange={(val, chk) => setFormValue(pre => ({ ...pre, firebaseTokenIsInvalid: chk }))}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col span={{xs: 24, xl:12}}>
              <Form.Group>
                <Form.Label>Language</Form.Label>
                <Form.Control name="language" value={formValue.language ?? ''} />
              </Form.Group>
            </Col>
            <Col span={{xs: 24, xl:12}}>
              <Form.Group>
                <Form.Label>Nationality</Form.Label>
                <Form.Control name="nationality" placeholder="ISO 3166 country code (e.g. US, DE, IR)" value={formValue.nationality ?? ''} />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col span={{xs: 24, xl:12}}>
              <Form.Group>
                <Form.Label>City</Form.Label>
                <Form.Control name="cityId" block
                  accepter={FetchingSelect}
                  fetchData={(text, page) => getCitiesLookup(text, undefined, page)}
                  placeholder="Select a city"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col span={{xs: 24}}>
              <Form.Group>
                <Form.Label>Restricted Features</Form.Label>
                  <TagPicker 
                    data={FeatureRuleNameList} 
                    value={formValue.restrictedFeatures} 
                    onChange={val => setFormValue(pre => ({...pre, restrictedFeatures: val}))}
                  />
              </Form.Group>
            </Col>
          </Row>
        </Grid>
      </Form>
      <Divider />
      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom: 20 }}></ErrorMessage>}

      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>

  );
};

export default UserSettingForm;